import { useState, useEffect, useCallback } from 'react'
import { brandThemesApi } from '../services/brandThemesApi'
import { BrandTheme, BrandPaletteEntry, BrandThemeHistoryEntry } from '../types/gotypes'

export default function useBrandThemes(customer: string) {
  const [themes, setThemes] = useState<BrandTheme[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!customer) return
    setIsLoading(true)
    try {
      const data = await brandThemesApi.list(customer)
      setThemes(data)
    } catch {
      // ignore
    } finally {
      setIsLoading(false)
    }
  }, [customer])

  useEffect(() => {
    refresh()
  }, [refresh])

  const createTheme = useCallback(
    async (name: string, palette: Record<string, BrandPaletteEntry>) => {
      const created = await brandThemesApi.create(customer, { name, palette })
      setThemes((prev) => [...prev, created])
      return created
    },
    [customer]
  )

  const updateTheme = useCallback(
    async (id: string, name: string, palette: Record<string, BrandPaletteEntry>) => {
      const updated = await brandThemesApi.update(customer, id, { name, palette })
      setThemes((prev) => prev.map((t) => (t.id === id ? updated : t)))
      return updated
    },
    [customer]
  )

  const deleteTheme = useCallback(
    async (id: string) => {
      await brandThemesApi.remove(customer, id)
      setThemes((prev) => prev.filter((t) => t.id !== id))
    },
    [customer]
  )

  const replace = (updated: BrandTheme) =>
    setThemes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))

  const promoteTheme = useCallback(
    async (id: string) => { replace(await brandThemesApi.promote(customer, id)) },
    [customer]
  )

  const demoteTheme = useCallback(
    async (id: string) => { replace(await brandThemesApi.demote(customer, id)) },
    [customer]
  )

  const loadThemeHistory = useCallback(
    async (id: string): Promise<BrandThemeHistoryEntry[]> => brandThemesApi.history(customer, id),
    [customer]
  )

  return { themes, createTheme, updateTheme, deleteTheme, promoteTheme, demoteTheme, loadThemeHistory, isLoading }
}
